import { useMemo } from "react";
import { FileRejection, useDropzone } from "react-dropzone";

import { isPreviewNotReadyError, useAlertContext, useConvertToPdfConfirmation } from "@shared";
import { DROPZONE_ACCEPTED_FILES, DROPZONE_MAX_FILE_SIZE } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DocumentGroup, DocumentResponse } from "@/api";
import {
  getIncomingMoves,
  getOutgoingDocumentIds,
} from "@/common/components/layout/components/dropzone/dnd/DropZoneDnd.types";
import { usePendingDocumentMovesContext } from "@/common/components/layout/components/dropzone/dnd/PendingDocumentMovesContext";
import { useDocumentBulkActions } from "@/common/components/layout/components/dropzone/hooks/useDocumentBulkActions";
import { useTranslation } from "@/translations";

interface UseDropzoneSectionConfig {
  /** The panel zone this section represents — used to derive its pending in/out moves. */
  zone: DocumentGroup;
  queryKey: unknown[];
  queryFn: () => Promise<DocumentResponse[]>;
  deleteFn: (files: DocumentResponse[]) => Promise<unknown>;
  renameFn: (file: DocumentResponse, newFileName: string) => Promise<void>;
  createCaseNoteFn?: (file: DocumentResponse) => Promise<unknown>;
  convertToPdfFn: (file: DocumentResponse) => Promise<void>;
  fetchDownloadBlobFn: (file: DocumentResponse) => Promise<Blob>;
  fetchPreviewBlobFn: (file: DocumentResponse) => Promise<Blob>;
  invalidationKeys: unknown[][];
  caseNoteInvalidationKeys?: unknown[][];
  onDrop?: (accepted: File[], rejected: FileRejection[]) => void;
  uploadingFiles: File[];
  enabled?: boolean;
  select?: ((data: DocumentResponse[]) => DocumentResponse[]) | undefined;
}

export function useDropzoneSection(config: UseDropzoneSectionConfig) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useAlertContext();
  const confirmConvertToPdf = useConvertToPdfConfirmation();

  const { pendingMoves } = usePendingDocumentMovesContext();

  const { data: allFiles = [] } = useQuery<DocumentResponse[]>({
    queryKey: config.queryKey,
    queryFn: config.queryFn,
    enabled: config.enabled,
    select: config.select,
  });

  // Files pending a move out of this zone are hidden here (they show as pending rows at the
  // target), which also keeps them out of selection and bulk actions below.
  const outgoingIds = useMemo(() => getOutgoingDocumentIds(pendingMoves, config.zone), [pendingMoves, config.zone]);
  const files = useMemo(() => allFiles.filter((f) => !outgoingIds.has(f.documentId)), [allFiles, outgoingIds]);
  const pendingIncomingMoves = useMemo(() => getIncomingMoves(pendingMoves, config.zone), [pendingMoves, config.zone]);

  const bulk = useDocumentBulkActions<DocumentResponse>({
    files,
    deleteFn: config.deleteFn,
    fetchDownloadBlobFn: config.fetchDownloadBlobFn,
    invalidationKeys: config.invalidationKeys,
  });

  const renameMutation = useMutation({
    mutationFn: ({ file, newFileName }: { file: DocumentResponse; newFileName: string }) =>
      config.renameFn(file, newFileName),
    onSuccess: (_data, { newFileName }) => {
      for (const key of config.invalidationKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      showSuccess(t("dropzone.fileList.renameSuccess", { fileName: newFileName }));
    },
    onError: (_error, { file }) => {
      showError(t("dropzone.fileList.renameError", { fileName: file.fileName }));
    },
  });

  const createCaseNoteMutation = useMutation({
    mutationFn: (file: DocumentResponse) => {
      if (!config.createCaseNoteFn) {
        return Promise.reject(new Error("createCaseNoteFn is not configured"));
      }
      return config.createCaseNoteFn(file);
    },
    onSuccess: (_data, file) => {
      const keys = [...config.invalidationKeys, ...(config.caseNoteInvalidationKeys ?? [])];
      for (const key of keys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      bulk.deselect(file.documentId);
      showSuccess(t("dropzone.fileList.createCaseNoteSuccess", { fileName: file.fileName }));
    },
    onError: (_error, file) => {
      showError(t("dropzone.fileList.createCaseNoteError", { fileName: file.fileName }));
    },
  });

  const convertToPdfMutation = useMutation({
    mutationFn: config.convertToPdfFn,
    onSuccess: (_data, file) => {
      for (const key of config.invalidationKeys) {
        void queryClient.invalidateQueries({ queryKey: key });
      }
      showSuccess(t("dropzone.fileList.convertToPdfSuccess", { fileName: file.fileName }));
    },
    onError: (error, file) => {
      showError(
        isPreviewNotReadyError(error)
          ? t("dropzone.fileList.convertToPdfNotReady", { fileName: file.fileName })
          : t("dropzone.fileList.convertToPdfError", { fileName: file.fileName })
      );
    },
  });

  const handleConvertToPdf = (file: DocumentResponse) =>
    confirmConvertToPdf(file.fileName, () => convertToPdfMutation.mutate(file));

  const handleCreateCaseNote = config.createCaseNoteFn
    ? (file: DocumentResponse) => createCaseNoteMutation.mutate(file)
    : undefined;

  const handleRename = (file: DocumentResponse, newFileName: string) => {
    renameMutation.mutate({ file, newFileName });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: config.onDrop,
    accept: DROPZONE_ACCEPTED_FILES,
    maxSize: DROPZONE_MAX_FILE_SIZE,
  });

  return {
    files,
    pendingIncomingMoves,
    uploadingFiles: config.uploadingFiles,
    getRootProps,
    getInputProps,
    isDragActive,
    selectedFiles: bulk.selectedFiles,
    handleToggleSelect: bulk.handleToggleSelect,
    fileActions: {
      onRename: handleRename,
      onDelete: (file: DocumentResponse) => bulk.handleDelete([file]),
      onCreateCaseNote: handleCreateCaseNote,
      onConvertToPdf: handleConvertToPdf,
      onFetchDownloadBlob: config.fetchDownloadBlobFn,
      onFetchPreviewBlob: config.fetchPreviewBlobFn,
      isBusy: renameMutation.isPending || createCaseNoteMutation.isPending || bulk.isDeleting,
    },
    handleDelete: bulk.handleDelete,
    handleBulkDownload: bulk.handleBulkDownload,
    isBulkActionPending: bulk.isBulkActionPending,
  };
}
